import { stripFuturesExpiration, normalizeSymbol } from "../shared/utils";
import type { Trade } from "wasp/entities";

// Static pip size table. Key = normalized uppercase symbol.
const PIP_SIZE_MAP: Record<string, number> = {
  // Forex 4-decimal
  EURUSD: 0.0001, GBPUSD: 0.0001, AUDUSD: 0.0001, NZDUSD: 0.0001,
  USDCAD: 0.0001, USDCHF: 0.0001, EURGBP: 0.0001, EURAUD: 0.0001,
  EURCHF: 0.0001, EURNZD: 0.0001, GBPAUD: 0.0001, GBPCAD: 0.0001,
  GBPCHF: 0.0001, GBPNZD: 0.0001, AUDCAD: 0.0001, AUDCHF: 0.0001,
  AUDNZD: 0.0001, CADCHF: 0.0001, NZDCAD: 0.0001, NZDCHF: 0.0001,
  EURCAD: 0.0001, AUDSGD: 0.0001, USDSGD: 0.0001,
  // Forex JPY pairs
  USDJPY: 0.01, EURJPY: 0.01, GBPJPY: 0.01, AUDJPY: 0.01,
  NZDJPY: 0.01, CADJPY: 0.01, CHFJPY: 0.01, SGDJPY: 0.01,
  // Forex other
  USDMXN: 0.0001, USDZAR: 0.0001, USDNOK: 0.0001, USDSEK: 0.0001,
  USDTRY: 0.0001,
  // Futures US equity
  NQ: 0.25, MNQ: 0.25, ES: 0.25, MES: 0.25,
  RTY: 0.1, M2K: 0.1, YM: 1, MYM: 1,
  // Futures energy
  CL: 0.01, MCL: 0.01, NG: 0.001, RB: 0.0001, HO: 0.0001,
  // Futures metals
  GC: 0.10, MGC: 0.10, SI: 0.005, MSI: 0.005, PL: 0.10, PA: 0.05,
  HG: 0.0005,
  // Futures grains
  ZC: 0.25, ZW: 0.25, ZS: 0.25, ZL: 0.0001, ZM: 0.1,
  // Futures rates
  ZN: 0.015625, ZB: 0.03125, ZF: 0.0078125, GE: 0.005,
  // CFDs / Indices (point = pip for these)
  DAX: 1, GER40: 1, CAC40: 1, FTSE100: 1, UK100: 1,
  NAS100: 1, US30: 1, SPX500: 1, SP500: 1, JP225: 1,
  AUS200: 1, US2000: 1, DE30: 1,
  // Crypto
  BTCUSD: 1, BTCUSDT: 1, XBTUSD: 1,
  ETHUSD: 0.01, ETHUSDT: 0.01,
  SOLUSD: 0.001, SOLUSDT: 0.001,
  BNBUSD: 0.01, BNBUSDT: 0.01,
  XRPUSD: 0.0001, XRPUSDT: 0.0001,
};

function fallbackPipSize(entryPrice: number): number {
  if (entryPrice < 5) return 0.0001;
  if (entryPrice < 100) return 0.01;
  if (entryPrice < 1000) return 0.1;
  return 1.0;
}

export function getPipSize(symbol: string, entryPrice?: number): number {
  const normalized = stripFuturesExpiration(normalizeSymbol(symbol)).toUpperCase();
  const fromMap = PIP_SIZE_MAP[normalized];
  if (fromMap !== undefined) return fromMap;
  return fallbackPipSize(entryPrice ?? 0);
}

// Returns signed pips: positive = favorable move (long: up, short: down).
// Returns null if entry or exit price is missing.
export function calculatePips(trade: Trade): number | null {
  const entry = trade.entryPrice;
  const exit = trade.exitPrice;
  if (entry == null || exit == null) return null;
  const pipSize = getPipSize(trade.symbol, entry);
  const raw = (exit - entry) / pipSize;
  return trade.type === "long" ? raw : -raw;
}
